/**
 * Clickstream tracking for SALTY Control Tower.
 *
 * Usage anywhere in the app:
 *   const { track } = useClickstream();
 *   track('aop.cell.edit', { row_id: 'gross_revenue_incl_gst', old_value: 100, new_value: 200 });
 *
 * Events are queued and flushed to the backend every 5 seconds (or on page unload).
 * If the flush fails, events are re-queued so nothing is lost.
 */

import React, {
  createContext, useContext, useRef, useEffect, useCallback, useState,
} from 'react';
import { useLocation } from 'react-router-dom';
import api from '../lib/api';

// ── Event taxonomy ────────────────────────────────────────────────────────────
// These are the canonical event types used across the app.
export const EVENTS = {
  // Navigation
  PAGE_VIEW:              'page.view',
  TAB_SWITCH:             'page.tab.switch',

  // AOP Planning
  AOP_CELL_EDIT:          'aop.cell.edit',
  AOP_CHANNEL_EDIT:       'aop.channel.edit',
  AOP_CATEGORY_EDIT:      'aop.category.edit',
  AOP_FILTER_CHANNEL:     'aop.filter.channel',
  AOP_FILTER_CATEGORY:    'aop.filter.category',
  AOP_FILTER_PERIOD:      'aop.filter.period',
  AOP_FILTER_FY:          'aop.filter.fiscal_year',
  AOP_ROW_EXPAND:         'aop.row.expand',
  AOP_ROW_COLLAPSE:       'aop.row.collapse',
  AOP_EXPAND_ALL:         'aop.rows.expand_all',
  AOP_COLLAPSE_ALL:       'aop.rows.collapse_all',
  AOP_EDIT_MODE_ON:       'aop.edit_mode.on',
  AOP_EDIT_MODE_OFF:      'aop.edit_mode.off',
  AOP_PLAN_SAVE:          'aop.plan.save',
  AOP_VERSION_SAVE:       'aop.version.save',
  AOP_VERSION_VIEW:       'aop.version.view',
  AOP_VERSION_RESTORE:    'aop.version.restore',
  AOP_IMPORT_PREVIEW:     'aop.import.preview',
  AOP_IMPORT_CONFIRM:     'aop.import.confirm',
  AOP_EXPORT:             'aop.export',
  AOP_TEMPLATE_DOWNLOAD:  'aop.template.download',

  // Dashboard
  DASHBOARD_VIEW:         'dashboard.view',

  // Actuals
  ACTUALS_VIEW:           'actuals.view',

  // Variance
  VARIANCE_VIEW:          'variance.view',

  // Channels
  CHANNELS_VIEW:          'channels.view',
  CHANNELS_FILTER:        'channels.filter',

  // Health Radar
  HEALTH_VIEW:            'health.view',

  // Simulator
  SIMULATOR_RUN:          'simulator.run',

  // Reports
  REPORT_GENERATE:        'report.generate',

  // Settings
  SETTINGS_PROFILE_EDIT:  'settings.profile.edit',
  SETTINGS_PROFILE_SAVE:  'settings.profile.save',

  // Admin
  ADMIN_USER_VIEW:        'admin.user.view',
  ADMIN_USER_BLOCK:       'admin.user.block',
  ADMIN_USER_UNBLOCK:     'admin.user.unblock',
  ADMIN_USER_ROLE_CHANGE: 'admin.user.role_change',
  ADMIN_USER_DELETE:      'admin.user.delete',
  ADMIN_LOGS_VIEW:        'admin.logs.view',
  ADMIN_CLICKSTREAM_VIEW: 'admin.clickstream.view',

  // Auth
  AUTH_LOGOUT:            'auth.logout',

  // Number format
  FORMAT_CHANGE:          'ui.number_format.change',
};

const PAGE_TITLES = {
  '/dashboard':       'Executive Dashboard',
  '/aop':             'Annual Operating Plan',
  '/actuals':         'Actual Financials',
  '/variance':        'Variance Analysis',
  '/channels':        'Channel Performance',
  '/health':          'Business Health Radar',
  '/simulator':       'Decision Simulator',
  '/working-capital': 'Working Capital',
  '/settings':        'Settings',
};

function generateSessionId() {
  return `sess_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
}

// ── Context ───────────────────────────────────────────────────────────────────

const ClickstreamContext = createContext(null);

export const ClickstreamProvider = ({ children }) => {
  const queueRef    = useRef([]);
  const sessionId   = useRef(generateSessionId());
  const flushRef    = useRef(null);

  const flush = useCallback(async () => {
    if (queueRef.current.length === 0) return;

    // Don't attempt to flush if user is not authenticated
    const token = localStorage.getItem('salty_token');
    if (!token) {
      queueRef.current = [];   // discard pre-login events — nothing to track yet
      return;
    }

    const batch = [...queueRef.current];
    queueRef.current = [];
    try {
      await api.post('/clickstream', { events: batch });
    } catch (err) {
      if (err.response?.status === 401) {
        // Token expired or invalid — discard rather than loop
        queueRef.current = [];
        return;
      }
      // Re-queue only on transient failures (network, 5xx)
      queueRef.current = [...batch, ...queueRef.current];
    }
  }, []);

  // Auto-flush every 5 seconds
  useEffect(() => {
    flushRef.current = setInterval(flush, 5000);
    return () => clearInterval(flushRef.current);
  }, [flush]);

  // Flush on page unload
  useEffect(() => {
    const handleUnload = () => {
      if (queueRef.current.length === 0) return;
      // Use sendBeacon for reliability on page close
      const payload = JSON.stringify({ events: queueRef.current });
      navigator.sendBeacon
        ? navigator.sendBeacon('/api/clickstream', new Blob([payload], { type: 'application/json' }))
        : void 0;
    };
    window.addEventListener('beforeunload', handleUnload);
    return () => window.removeEventListener('beforeunload', handleUnload);
  }, []);

  // Flush when tab becomes hidden
  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === 'hidden') flush();
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [flush]);

  const track = useCallback((eventType, metadata = {}) => {
    queueRef.current.push({
      event_type: eventType,
      page:       window.location.pathname,
      session_id: sessionId.current,
      timestamp:  new Date().toISOString(),
      metadata,
    });
    // Immediate flush if queue grows large
    if (queueRef.current.length >= 20) flush();
  }, [flush]);

  return (
    <ClickstreamContext.Provider value={{ track, sessionId: sessionId.current }}>
      {children}
    </ClickstreamContext.Provider>
  );
};

export const useClickstream = () => {
  const ctx = useContext(ClickstreamContext);
  if (!ctx) throw new Error('useClickstream must be used within ClickstreamProvider');
  return ctx;
};

// ── Route tracker (mount inside BrowserRouter) ────────────────────────────────

export function RouteTracker() {
  const location = useLocation();
  const { track } = useClickstream();

  useEffect(() => {
    track(EVENTS.PAGE_VIEW, {
      path:  location.pathname,
      title: PAGE_TITLES[location.pathname] || location.pathname,
    });
  }, [location.pathname]); // eslint-disable-line

  return null;
}
